import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import { visitorAPI, villaAPI, organizationAPI, noticeAPI, vehicleAPI } from '../../services/api';
import { useSettings } from '../../contexts/SettingsContext';
import { formatMySQLDateTime } from '../../utils/dateUtils';
// import { getSocietyName } from '../../utils/brandingUtils';
import '../../styles/Dashboard.css';
import '../../styles/ThemeOverrides.css';
import '../../styles/GuardTheme.css';
import PageTitle from '../shared/PageTitle';

const Dashboard = () => {
  const { currentUser } = useContext(AuthContext);
  const { settings, loadSettings } = useSettings();
  const [loading, setLoading] = useState(true);
  const [todayCount, setTodayCount] = useState(0);
  const [pendingVisitors, setPendingVisitors] = useState([]); // Active visitors pending checkout
  // const [activeVisitors, setActiveVisitors] = useState([]);
  const [villas, setVillas] = useState([]);
  const [orgSettings, setOrgSettings] = useState(null);
  const [rules, setRules] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [localVisitors, setLocalVisitors] = useState([]);
  const [unitsLoaded, setUnitsLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [localVillas, setLocalVillas] = useState([]);
  const [vehicleCount, setVehicleCount] = useState(0);

  const processUnitData = (unit) => {
    return {
      ...unit,
      id: unit.id || null,
      villa_number: unit.villa_number || '',
      organization_id: unit.organization_id || null
    };
  };
  // Initial data loading effect - make it safe and reliable
  useEffect(() => {
    let isMounted = true;
    let retryCount = 0;
    const MAX_RETRIES = 3;
    let intervalId = null;

    const loadVisitors = async () => {
      try {
        if (!isMounted) return;

        setLoading(true);
        setError(null);
        console.log(error);
        console.log(localVillas);

        const user = JSON.parse(sessionStorage.getItem('user') || '{}');
        const organizationId = user.organization_id;

        if (!organizationId) {
          setError('Organization context not found. Please try logging out and back in.');
          setLoading(false);
          return;
        }

        // Common request headers
        const headers = {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        };

        // Force localhost in development, ignore environment variable if it points to production
        const baseUrl = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
          ? 'http://1'
          : (process.env.REACT_APP_API_URL || 'https://nivaas.app');

        // Load units first if not already loaded
        if (!unitsLoaded) {
          try {
            // console.log('Loading units...'); // Reduced logging
            const unitsResponse = await axios.get(`${baseUrl}/api/units`, { headers });
            if (isMounted && unitsResponse?.data) {
              try {
                const processedUnits = Array.isArray(unitsResponse.data)
                  ? unitsResponse.data.map(unit => processUnitData(unit || {}))
                  : [];
                setLocalVillas(processedUnits);
                setUnitsLoaded(true);
                // console.log('Units loaded successfully:', processedUnits.length); // Reduced logging
              } catch (processingError) {
                console.error('Error processing units data:', processingError);
                // Continue with empty units rather than failing
                setLocalVillas([]);
              }
            }
          } catch (unitsError) {
            console.error('Error loading units:', unitsError);
            if (retryCount < MAX_RETRIES) {
              retryCount++;
              console.log(`Retrying units load... Attempt ${retryCount}`);
              await new Promise(resolve => setTimeout(resolve, 1000));
              if (isMounted) {
                return loadVisitors();
              }
              return;
            }
            setError('Failed to load units. Please refresh the page.');
            setLoading(false);
            return;
          }
        }

        // Get visitors with status 'active', 'approved', or 'upcoming'
        try {
          // console.log('Fetching active, approved, and upcoming visitors...'); // Reduced logging
          const activeResponse = await axios.get(`${baseUrl}/api/visitors/search`, {
            params: {
              status: ['active', 'approved', 'upcoming'],
              include_organization: true,
              organization_id: organizationId,
              limit: 500
            },
            headers
          });

          // Process active visitors
          const responseData = activeResponse?.data || {};
          const activeVisitors = Array.isArray(responseData) ? responseData :
            responseData?.visitors ? responseData.visitors : [];

          // Debug log visitor data - only when there are issues
          if (activeVisitors.length === 0) {
            console.log('No active visitors found');
          } else {
            // console.log('Loaded visitors:', activeVisitors.length); // Reduced logging
          }

          if (isMounted) {
            // Update local state with active visitors
            setLocalVisitors(activeVisitors);
            setError(null);
          }
        } catch (visitorError) {
          console.error('Error fetching visitors:', visitorError);
          if (isMounted) {
            setError('Failed to load visitor data. Please try again.');
          }
        }

        // Get vehicle count for organization
        try {
          const vehicleResponse = await vehicleAPI.getOrgVehicles();
          if (isMounted && vehicleResponse?.data) {
            setVehicleCount(vehicleResponse.data.length);
          }
        } catch (vehicleError) {
          console.error('Error fetching vehicle count:', vehicleError);
          // Don't set error for vehicle count failure, just log it
        }
      } catch (err) {
        console.error('Error in load function:', err);
        if (isMounted) {
          setError('An unexpected error occurred. Please refresh the page.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    // Load visitors immediately and set up refresh interval
    loadVisitors();
    intervalId = setInterval(loadVisitors, 120000); // Refresh every 2 minutes (reduced from 30 seconds)

    return () => {
      isMounted = false;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [unitsLoaded]);

  // Helper function to apply styles directly to DOM elements
  const applyToDOMElements = (colors) => {
    try {
      // Apply to sidebar
      const sidebar = document.querySelector('.sidebar-navigation');
      if (sidebar) {
        sidebar.style.backgroundColor = colors.sidebarBgColor;

        // Style all menu items
        const menuItems = sidebar.querySelectorAll('.menu-item:not(.active)');
        menuItems.forEach(item => {
          const icon = item.querySelector('.menu-icon');
          const text = item.querySelector('.menu-text');

          if (icon) icon.style.color = colors.sidebarTextColor;
          if (text) text.style.color = colors.sidebarTextColor;
        });

        // Style active menu items
        const activeItems = sidebar.querySelectorAll('.menu-item.active, .active');
        activeItems.forEach(item => {
          item.style.backgroundColor = colors.sidebarActiveBgColor;

          const itemIcon = item.querySelector('.menu-icon');
          const itemText = item.querySelector('.menu-text');

          if (itemIcon) itemIcon.style.color = colors.sidebarActiveTextColor;
          if (itemText) itemText.style.color = colors.sidebarActiveTextColor;
        });
      }

      // Apply to card headers
      const cardHeaders = document.querySelectorAll('.card-header');
      cardHeaders.forEach(header => {
        header.style.backgroundColor = colors.blockTitleBgColor;
        header.style.color = colors.blockTitleColor;
      });

      // Apply to buttons
      const primaryButtons = document.querySelectorAll('.btn-primary');
      primaryButtons.forEach(button => {
        button.style.backgroundColor = colors.primaryColor;
        button.style.borderColor = colors.primaryColor;
      });

      // console.log('Applied theme directly to DOM elements');
    } catch (error) {
      console.error('Error applying theme directly to DOM elements:', error);
    }
  };

  // Move applyLocalTheme function to component level and wrap in useCallback
  const applyThemeDirectly = () => {
    try {
      // console.log('--------- APPLYING THEME DIRECTLY FROM DATABASE ---------');
      // console.log('Current orgSettings:', orgSettings);

      if (!orgSettings) {
        // console.log('No organization settings available yet');
        return;
      }

      // Get theme values directly from orgSettings
      const themeColors = {
        primaryColor: orgSettings.theme_primary_color || '#0d6efd',
        secondaryColor: orgSettings.theme_secondary_color || '#6c757d',
        sidebarBgColor: orgSettings.sidebar_bg_color || '#ffffff',
        sidebarTextColor: orgSettings.sidebar_text_color || '#212529',
        sidebarActiveBgColor: orgSettings.sidebar_active_bg_color || '#0d6efd',
        sidebarActiveTextColor: orgSettings.sidebar_active_text_color || '#ffffff',
        blockTitleColor: orgSettings.block_title_color || '#8B4513',
        blockTitleBgColor: orgSettings.block_title_bg_color || '#f8f9fa',
      };

      // console.log('Theme colors to apply:', themeColors);

      // Apply theme settings globally - COMMENTED OUT TO PREVENT INLINE STYLES
      // const root = document.documentElement;
      // root.style.setProperty('--primary-color', themeColors.primaryColor);
      // root.style.setProperty('--bs-primary', themeColors.primaryColor);
      // root.style.setProperty('--secondary-color', themeColors.secondaryColor);
      // root.style.setProperty('--bs-secondary', themeColors.secondaryColor);
      // root.style.setProperty('--sidebar-bg-color', themeColors.sidebarBgColor);
      // root.style.setProperty('--sidebar-text-color', themeColors.sidebarTextColor);
      // root.style.setProperty('--sidebar-active-bg-color', themeColors.sidebarActiveBgColor);
      // root.style.setProperty('--sidebar-active-text-color', themeColors.sidebarActiveTextColor);
      // root.style.setProperty('--block-title-color', themeColors.blockTitleColor);
      // root.style.setProperty('--block-title-bg-color', themeColors.blockTitleBgColor);

      // Direct DOM styling for immediate visual feedback
      applyToDOMElements(themeColors);

      // Apply again after a short delay to catch any delayed rendering
      setTimeout(() => applyToDOMElements(themeColors), 300);

      // console.log('Theme applied successfully');
      // console.log('--------------------------------');
    } catch (error) {
      console.error('Error in applyThemeDirectly:', error);
    }
  };

  // Debug function to get computed CSS variable values
  const getComputedCssVars = () => {
    const cssVars = {};
    const root = document.documentElement;
    const computed = getComputedStyle(root);

    // Get important theme variables
    const vars = [
      '--primary-color',
      '--secondary-color',
      '--bs-primary',
      '--sidebar-bg-color',
      '--sidebar-text-color',
      '--sidebar-active-bg-color',
      '--sidebar-active-text-color',
      '--block-title-color',
      '--block-title-bg-color'
    ];

    vars.forEach(varName => {
      cssVars[varName] = computed.getPropertyValue(varName);
    });

    return cssVars;
  };

  useEffect(() => {
    // Collect debug data
    const collectDebugData = async () => {
      try {
        // Create debug object
        const debug = {
          settings: { ...settings },
          user: { ...currentUser },
          computedCss: getComputedCssVars(),
          document_theme: document.documentElement.getAttribute('data-bs-theme'),
          currentOrgSettings: {}
        };

        // Current theme settings from org settings
        if (orgSettings) {
          debug.currentOrgSettings = {
            theme_primary_color: orgSettings.theme_primary_color || 'not set',
            theme_secondary_color: orgSettings.theme_secondary_color || 'not set',
            sidebar_bg_color: orgSettings.sidebar_bg_color || 'not set',
            sidebar_text_color: orgSettings.sidebar_text_color || 'not set',
            sidebar_active_bg_color: orgSettings.sidebar_active_bg_color || 'not set',
            sidebar_active_text_color: orgSettings.sidebar_active_text_color || 'not set',
            block_title_color: orgSettings.block_title_color || 'not set',
            block_title_bg_color: orgSettings.block_title_bg_color || 'not set',
            society_name: orgSettings.society_name || 'not set'
          };
        }

        // Try to get organization data
        try {
          if (currentUser?.organization_id && !orgSettings) {
            // Only fetch if we don't already have orgSettings
            const orgResponse = await organizationAPI.getOrganizationSettings(currentUser.organization_id);
            setOrgSettings(orgResponse.data);
            debug.organization = orgResponse.data;
          }
        } catch (error) {
          debug.orgError = error.message;
        }

      } catch (e) {
        console.error('Error collecting debug data:', e);
      }
    };

    collectDebugData();
  }, [currentUser, settings]);

  // Load organization settings and ensure we have the latest settings
  useEffect(() => {
    const loadOrgSettings = async () => {
      try {
        if (currentUser && currentUser.organization_id) {
          // console.log('Loading organization settings for guard dashboard...');
          const orgResponse = await organizationAPI.getOrganizationSettings(currentUser.organization_id);
          console.log('hereeeee');

          // Update local state with organization settings
          setOrgSettings(orgResponse.data);

          // Apply the theme immediately
          applyThemeDirectly();
        }
      } catch (error) {
        console.error('Error loading organization settings:', error);
      }
    };

    loadSettings(); // Load settings from context
    loadOrgSettings(); // Load direct organization settings

    // Refresh organization settings every 5 minutes to pick up changes from database
    const refreshInterval = setInterval(() => {
      loadOrgSettings();
    }, 300000);

    // Clear interval on component unmount
    return () => clearInterval(refreshInterval);
  }, [currentUser]);

  // Function to fetch all guard dashboard data
  const fetchGuardData = async () => {
    try {
      setLoading(true);

      // Fetch comprehensive visitor statistics
      try {
        const statsRes = await visitorAPI.getComprehensiveStats();

        if (statsRes.data) {
          setTodayCount(statsRes.data.todayCount || 0);
        } else {
          setTodayCount(0);
        }
      } catch (error) {
        console.error('Error fetching visitor statistics:', error);
        setTodayCount(0);
      }

      // Fetch pending visitors (still in premises)
      try {
        const pendingVisitorsRes = await visitorAPI.getPendingVisitors();

        // Handle different API response formats
        let processedPendingVisitors = [];

        if (pendingVisitorsRes.data && pendingVisitorsRes.data.pendingCount !== undefined) {
          processedPendingVisitors = Array.isArray(pendingVisitorsRes.data.byUnit) ? pendingVisitorsRes.data.byUnit : [];
        } else if (pendingVisitorsRes.data && Array.isArray(pendingVisitorsRes.data) && pendingVisitorsRes.data[0] && pendingVisitorsRes.data[0].count !== undefined) {
          try {
            const alternativeResponse = await visitorAPI.getPendingVisitors();
            if (alternativeResponse.data && Array.isArray(alternativeResponse.data)) {
              processedPendingVisitors = alternativeResponse.data.filter(v => {
                return v.visitor_name &&
                  !v.visitor_name.includes('Fallback') &&
                  !v.visitor_name.includes('Mock') &&
                  !v.visitor_name.includes('Error') &&
                  !v.visitor_name.includes('Demo') &&
                  !v.visitor_name.includes('[DEV]') &&
                  !v.villa_number?.includes('Demo') &&
                  !v.villa_number?.includes('Development');
              });
            }
          } catch (altError) {
            console.error('Alternative pending visitors endpoint failed:', altError);
            processedPendingVisitors = [];
          }
        } else if (pendingVisitorsRes.data && Array.isArray(pendingVisitorsRes.data)) {
          processedPendingVisitors = pendingVisitorsRes.data.filter(v => {
            return v.visitor_name &&
              !v.visitor_name.includes('Fallback') &&
              !v.visitor_name.includes('Mock') &&
              !v.visitor_name.includes('Error') &&
              !v.visitor_name.includes('Demo') &&
              !v.visitor_name.includes('[DEV]') &&
              !v.villa_number?.includes('Demo') &&
              !v.villa_number?.includes('Development');
          });
        } else {
          processedPendingVisitors = [];
        }

        // Filter out fallback/demo data
        processedPendingVisitors = processedPendingVisitors.filter(v => {
          return v.visitor_name &&
            !v.visitor_name.includes('Demo') &&
            !v.visitor_name.includes('Fallback') &&
            !v.visitor_name.includes('Error') &&
            !v.visitor_name.includes('[DEV]') &&
            !v.villa_number?.includes('Demo') &&
            !v.villa_number?.includes('Development');
        });

        setPendingVisitors(processedPendingVisitors);
      } catch (error) {
        console.error('Error fetching pending visitors:', error);
        setPendingVisitors([]);
      }

      // Fetch all villas for reference
      try {
        const villasRes = await villaAPI.getAllVillas();
        setVillas(villasRes.data || []);
      } catch (error) {
        console.error('Error fetching villas:', error);
        setVillas([]);
      }
    } catch (error) {
      console.error('Error in guard data fetch operation:', error);
    } finally {
      setLoading(false);
    }
  };

  // Custom styles based on theme settings
  const blockTitleStyle = {
    backgroundColor: settings.block_title_bg_color || '#f8f9fa',
    color: settings.block_title_color || '#8B4513'
  };

  // Add back the theme listener useEffect that applies theme on changes  
  useEffect(() => {
    // Apply theme immediately
    applyThemeDirectly();

    // Also apply when settings change
    const handleThemeChange = () => {
      // console.log('Theme change event detected, reapplying theme');
      applyThemeDirectly();
    };

    window.addEventListener('apply-organization-theme', handleThemeChange);

    return () => {
      window.removeEventListener('apply-organization-theme', handleThemeChange);
    };
  }, [orgSettings, applyThemeDirectly]);

  useEffect(() => {
    fetchGuardData();

    // Refresh data every 5 minutes (reduced frequency)
    const interval = setInterval(fetchGuardData, 300000);

    return () => clearInterval(interval);
  }, []);

  const handleCheckout = async (visitorId) => {
    try {
      // Using the imported formatMySQLDateTime function

      // Send checkout request to API
      await visitorAPI.checkoutVisitor(visitorId, {
        time_out: formatMySQLDateTime(new Date()),
        status: 'completed'
      });

      console.log('Successfully checked out visitor:', visitorId);

      // Remove from the pending list in state
      setPendingVisitors(prevPendingVisitors =>
        prevPendingVisitors.filter(v => v.id !== visitorId)
      );

      // Refresh the data to ensure everything is up-to-date
      fetchGuardData();
      getActiveVisitors();

      // Show success message
      alert('Visitor successfully checked out');
      // 🔄 Reload the page to refresh all data
      window.location.reload();
    } catch (error) {
      console.error('Error checking out visitor:', error);
      alert('Failed to check out visitor. Please try again.');
    }
  };

  const getActiveVisitors = useCallback(() => {
    return localVisitors.filter(visitor => {
      const isActive = (visitor.status === 'active' || (visitor.status === 'approved' && (visitor.time_in || visitor.approved_by))) && !visitor.time_out;

      if (isActive) {
        console.log('Active visitor found:', {
          id: visitor.id,
          name: visitor.visitor_name,
          status: visitor.status,
          time_in: visitor.time_in,
          time_out: visitor.time_out,
          purpose: visitor.purpose,
          villa: visitor.villa_number
        });
      }

      return isActive;
    });
  }, [localVisitors]);

  // Fetch notices periodically
  useEffect(() => {
    const fetchNotices = async () => {
      try {
        const [rulesRes, annRes] = await Promise.all([
          noticeAPI.getRules(),
          noticeAPI.getAnnouncements()
        ]);
        setRules(rulesRes.data);
        setAnnouncements(annRes.data);
      } catch (err) {
        console.error('Error fetching notices:', err);
      }
    };
    fetchNotices();
    // Fetch notices every 10 minutes instead of 30 seconds
    const interval = setInterval(fetchNotices, 600000);
    return () => clearInterval(interval);
  }, []);

  function formatDateTime(dateString) {
    if (!dateString || typeof dateString !== 'string') return 'N/A';

    try {
      // Parse "YYYY-MM-DD HH:mm:ss" as UTC
      const [datePart, timePart] = dateString.split(' ');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hour, minute, second] = timePart.split(':').map(Number);

      // Create a UTC Date object
      const utcDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second));

      // Format to IST using timeZone
      return new Intl.DateTimeFormat('en-IN', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      }).format(utcDate);
    } catch (err) {
      console.error('Invalid date string:', dateString, err);
      return 'Invalid Date';
    }
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center mt-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }
  console.log('pendingVisitors', pendingVisitors);
  return (
    <div className="container-fluid mt-4 activity-container">
      {/* Debug Panel removed */}

      {/* Header with refresh button */}
      <PageTitle
        icon="fa-shield-alt"
        title="Security Dashboard"
        action={
          <div className="d-flex align-items-center">
            <button
              className="btn btn-outline-primary me-3"
              onClick={() => fetchGuardData()}
              title="Refresh data"
            >
              <i className="fas fa-sync-alt"></i> Refresh
            </button>
            <span className="badge bg-info me-2">Security Guard</span>
          </div>
        }
      />

      {/* Welcome message */}
      <div className="alert alert-info mb-4">
        <div className="d-flex align-items-center">
          <i className="fas fa-info-circle fa-2x me-3"></i>
          <div>
            <h5 className="mb-1">Security Management Portal</h5>
            <p className="mb-0">Monitor and manage visitors to ensure community safety and security.</p>
          </div>
        </div>
      </div>

      {/* Today's Stats - Using dashboard-stats-container */}
      <div className="dashboard-stats-container">
        <div className="dashboard-stat-card visitors">
          <div className="stat-icon">
            <i className="fas fa-users"></i>
          </div>
          <div className="stat-content">
            <div className="stat-value">{todayCount}</div>
            <div className="stat-label">Today&apos;s Visitors</div>
          </div>
        </div>

        <div className="dashboard-stat-card pending">
          <div className="stat-icon">
            <i className="fas fa-user-clock"></i>
          </div>
          <div className="stat-content">
            <div className="stat-value">{getActiveVisitors().length}</div>
            <div className="stat-label">Active Visitors</div>
          </div>
        </div>

        <div className="dashboard-stat-card units">
          <div className="stat-icon">
            <i className="fas fa-home"></i>
          </div>
          <div className="stat-content">
            <div className="stat-value">{villas.length}</div>
            <div className="stat-label">Total Units</div>
          </div>
        </div>

        <div className="dashboard-stat-card vehicles">
          <div className="stat-icon">
            <i className="fas fa-car"></i>
          </div>
          <div className="stat-content">
            <div className="stat-value">{vehicleCount}</div>
            <div className="stat-label">Registered Vehicles</div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card dashboard-card">
            <div className="card-header" style={blockTitleStyle}>
              <h5 className="mb-0">Quick Actions</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-3 mb-3">
                  <Link to="/guard/visitors" className="btn btn-primary w-100 d-flex align-items-center justify-content-between">
                    <span>Register New Visitor</span>
                    <i className="fas fa-user-plus"></i>
                  </Link>
                </div>
                <div className="col-md-3 mb-3">
                  <Link to="/guard/visitor-search" className="btn btn-warning w-100 d-flex align-items-center justify-content-between">
                    <span>Search Visitor Records</span>
                    <i className="fas fa-search"></i>
                  </Link>
                </div>
                <div className="col-md-3 mb-3">
                  <a href="#pending-visitors" className="btn btn-success w-100 d-flex align-items-center justify-content-between">
                    <span>Manage Active Visitors</span>
                    <i className="fas fa-user-clock"></i>
                  </a>
                </div>
                <div className="col-md-3 mb-3">
                  <Link to="/guard/vehicles" className="btn btn-info w-100 d-flex align-items-center justify-content-between">
                    <span>View All Vehicles</span>
                    <i className="fas fa-car"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* System Info */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card dashboard-card">
            <div className="card-header" style={blockTitleStyle}>
              <h5 className="mb-0">System Information</h5>
            </div>
            <div className="card-body">
              <div className="alert alert-info mt-3">
                <i className="fas fa-info-circle me-2"></i>
                You are logged in as a Security Guard with visitor management permissions.
              </div>
              <div className="mt-3">
                <p className="small text-muted mb-1">Features available:</p>
                <ul className="small text-muted">
                  <li>Register new visitors</li>
                  <li>Checkout visitors when they leave</li>
                  <li>View visitor history</li>
                  <li>Search for visitor records</li>
                  <li>View registered vehicles in the organization</li>
                </ul>
              </div>
              <div className="mt-4 text-center">
                <Link to="/profile" className="btn btn-sm btn-outline-secondary">
                  <i className="fas fa-user-cog me-2"></i> Manage Profile
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pending Visitors Section */}
      <div className="row mt-4" id="pending-visitors">
        <div className="col-12">
          <div className="card">
            <div className="card-header" style={blockTitleStyle}>
              <h5 className="mb-0">
                <i className="fas fa-user-clock me-2"></i>
                Active Visitors - Pending Checkout ({getActiveVisitors().length})
              </h5>
            </div>
            <div className="card-body">
              {getActiveVisitors().length === 0 ? (
                <div className="alert alert-success">
                  <i className="fas fa-check-circle me-2"></i>
                  No active visitors. All visitors have checked out.
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover table-striped">
                    <thead>
                      <tr>
                        <th>Visitor Name</th>
                        <th>Villa</th>
                        <th>Check In Time</th>
                        <th>Purpose</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getActiveVisitors().map(visitor => (
                        <tr key={visitor.id}>
                          <td className="align-middle">{visitor.visitor_name}</td>
                          <td className="align-middle">{visitor.unit_number || visitor.villa_number || 'N/A'}</td>
                          <td className="align-middle">{visitor.time_in ? formatDateTime(visitor.time_in) : 'Not checked in'}</td>
                          <td className="align-middle">{visitor.purpose || 'Not specified'}</td>
                          <td>
                            <button
                              className="btn btn-success btn-sm"
                              onClick={() => handleCheckout(visitor.id)}
                              disabled={!visitor.time_in}
                            >
                              <i className="fas fa-sign-out-alt me-2"></i>
                              Check Out
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="mt-3 text-end">
                <Link to="/guard/visitors" className="btn btn-primary">
                  <i className="fas fa-list me-2"></i>
                  Manage All Visitors
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Notices Section */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header" style={blockTitleStyle}>
              <h5 className="mb-0">Notices</h5>
            </div>
            <div className="card-body">
              <h6 className="mt-2"><strong>Rules</strong></h6>
              {rules.length === 0 ? (
                <p>No rules available.</p>
              ) : (
                <ul className="list-group mb-3">
                  {rules.map(rule => (
                    <li key={rule.id} className="list-group-item d-flex justify-content-between align-items-center">
                      <div>
                        <strong>{rule.title}</strong>
                        <p className="mb-0">{rule.description}</p>
                        <small className="text-muted">{new Date(rule.created_at).toLocaleDateString()}</small>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <h6 className="mt-2"><strong>Announcements</strong></h6>
              {announcements.length === 0 ? (
                <p>No announcements available.</p>
              ) : (
                <ul className="list-group">
                  {announcements.map(ann => (
                    <li key={ann.id} className="list-group-item d-flex justify-content-between align-items-center">
                      <div>
                        <strong>{ann.title}</strong>
                        <p className="mb-0">{ann.description}</p>
                        <small className="text-muted">
                          {new Date(ann.start_date).toLocaleDateString()}{ann.expiry_date ? ` - ${new Date(ann.expiry_date).toLocaleDateString()}` : ''}
                        </small>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 